import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, SvgIcon } from '@material-ui/core';
import { MonthPicker, LineSelector, FilterMenu, AttributesSelector } from '..';
import {
  SVG_ICON_PATHS,
  DISPLAY_MESSAGES,
  USER_OPERATIONS,
  APPLICATION_VARIANTS,
  DATE_FORMAT,
} from '../../constants';
import defaultStyles from './ToolBar.module.css';
import jlrStyles from './ToolBarJlr.module.css';
import { FilterIcon } from '../../icons';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { AiFillFile } from 'react-icons/ai';
import { IoMdCloudDownload } from 'react-icons/io';
import { IoMdCloudUpload } from 'react-icons/io';
import { MdClose } from 'react-icons/md';
import { Prompt } from 'react-router-dom';
import {
  callFunctionConditionallyWithWarningCurry,
  getFilterOptionsByBU,
  setStylesAccordingToPlant,
  exportToExcel,
  getDatesForRange,
  downloadCsv,
} from '../../utils/utils';
import AuthEnabledButton from '../AuthEnabledButton/AuthEnabledButton';
import config from '../../config/config';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { parseAsync } from 'json2csv';
import ExcelJS from 'exceljs';
import { buildErrorMessage } from '../../apis/calls';

// Static optional attribute definitions for CSV/Excel export
const OPTIONAL_ATTRIBUTE_COLUMNS = [
  { title: 'Family', field: 'family' },
  { title: 'Order Frequency', field: 'orderFrequency' },
  { title: 'Power Steering', field: 'powerSteering' },
  { title: 'Build Card Number', field: 'buildCardNumber' },
  { title: 'Market', field: 'market' },
  { title: 'Category', field: 'category' },
  { title: 'Country', field: 'country' },
  { title: 'Defence', field: 'defence' },
  { title: 'FBV', field: 'fbv' },
  { title: 'FBV Vendor Code', field: 'fbvVendorCode' },
  { title: 'FBV Vendor Name', field: 'fbvVendorName' },
  { title: 'Colour', field: 'colour' },
  { title: 'Evr Number', field: 'evrNumber' },
];

const MANDATORY_ATTRIBUTE_FIELDS = new Set(['market', 'country', 'evrNumber', 'colourCode', 'colour']);

/**
 * Safely extracts plan quantity for a specific date from a VC row object.
 */
const getPlanQuantity = (row, dateISO) => {
  let val;
  if (row.plan && row.plan[dateISO] !== undefined) {
    val = row.plan[dateISO];
  } else if (row[dateISO] !== undefined) {
    val = row[dateISO];
  }
  return typeof val === 'object' ? (val?.quantity ?? 0) : (val ?? 0);
};

const ToolBar = ({
  applyColumnsSelection,
  applyFilters,
  disableFilter,
  disableMonthPicker,
  disableUpload,
  handleFileUpload,
  handleMonthSelect,
  hasActiveEdits,
  lastUpdatedDate,
  optionalColumns,
  planData,
  restrictNavigationDuringEditing,
  selectedOptionalCols,
  callingAverage,
  line,
}) => {
  const timeString = moment(lastUpdatedDate).format('hh:mma');
  const dateString = moment(lastUpdatedDate).format('DD MMMM YYYY');

  const [styles, setStyles] = useState(defaultStyles);
  const [plant] = usePlantAndLinesStore().plantState;
  const isLucknowProlifePlant = plant.label == "Lucknow-Prolife"

  const filterRef = useRef(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [
    isDownloadOrUploadDialogOpen,
    setIsDownloadOrUploadDialogOpen,
  ] = useState(false);

  const handleFilterOpen = () => setIsFilterOpen(true);
  const handleFilterClose = () => setIsFilterOpen(false);
  const handleFilter = filters => {
    applyFilters(filters);
    handleFilterClose();
  };
  const showDownloadAndUploadDialog = () => {
    setIsDownloadOrUploadDialogOpen(true);
  };
  const handleDonloadOrUploadDialogueClose = () => {
    setIsDownloadOrUploadDialogOpen(false);
  };

  const handleDownloadTemplateButtonClick = () => {
    const link = document.createElement('a');
    link.setAttribute('download', `Day-wise-upload-plan-template.xlsx`);
    exportToExcel(config.APPLICATION_VARIANT).then(dataUri => {
      link.href = dataUri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownloadOrUploadDialogOpen(false);
    });
  };

  const handleDayWisePlanDownload = async (planData, selectedOptionalCols = []) => {
    if (!planData) return;

    const rawRows = (planData.filteredData && planData.filteredData.length > 0)
      ? planData.filteredData
      : (planData.data || []);
    if (rawRows.length === 0) return;

    const dataRows = rawRows.map(row => ({
      ...row,
      ...(row.attributes || {}),
    }));

    const dates = (planData.startDate && planData.endDate)
      ? getDatesForRange(planData.startDate, planData.endDate)
      : [];
    const dateISOs = dates.map(d => d.format(DATE_FORMAT.ISO));

    // Calculate monthly totals and daily totals in a single pass
    let monthlyTarget = 0;
    let monthlyBalance = 0;
    let monthlyArchived = 0;
    const dailyTotals = {};
    dateISOs.forEach(iso => { dailyTotals[iso] = 0; });

    dataRows.forEach(row => {
      monthlyTarget += Number(row.total) || 0;
      monthlyBalance += Number(row.balance) || 0;
      monthlyArchived += Number(row.archived) || 0;

      dateISOs.forEach(iso => {
        dailyTotals[iso] += Number(getPlanQuantity(row, iso)) || 0;
      });
    });

    // 1. Base Column Definitions
    const baseHeaders = [
      {
        header: isLucknowProlifePlant ? 'Engine' : 'Vc',
        key: 'vc',
        width: 20,
        getValue: row => (row.isTotalRow ? 'Total' : row.vc || ''),
      },
      {
        header: 'Description',
        key: 'description',
        width: 32,
        getValue: row => (row.isTotalRow ? '' : row.description || ''),
      },
      {
        header: 'Target',
        key: 'total',
        width: 12,
        getValue: row => row.total ?? '',
      },
      {
        header: 'Balance',
        key: 'balance',
        width: 12,
        getValue: row => row.balance ?? '',
      },
      {
        header: 'Archived',
        key: 'archived',
        width: 12,
        getValue: row => row.archived ?? 0,
      },
    ];

    // 2. Mandatory Attributes (after main headers, before daywise headers)
    const mandatoryHeaders = [
      {
        header: 'market',
        key: 'market',
        width: 14,
        getValue: row => (row.isTotalRow ? '' : (row.market ?? row.attributes?.market ?? '')),
      },
      {
        header: 'country',
        key: 'country',
        width: 16,
        getValue: row => (row.isTotalRow ? '' : (row.country ?? row.attributes?.country ?? '')),
      },
      {
        header: 'evrNumber',
        key: 'evrNumber',
        width: 16,
        getValue: row => (row.isTotalRow ? '' : (row.evrNumber ?? row.attributes?.evrNumber ?? '')),
      },
      {
        header: 'colourCode',
        key: 'colourCode',
        width: 16,
        getValue: row =>
          row.isTotalRow
            ? ''
            : (row.colourCode ?? row.attributes?.colourCode ?? row.colour ?? row.attributes?.colour ?? ''),
      },
    ];

    // 3. User Selected Optional Attributes (excluding mandatory ones)
    const optionalHeaders = OPTIONAL_ATTRIBUTE_COLUMNS
      .filter(
        attr =>
          selectedOptionalCols.includes(attr.title) &&
          !MANDATORY_ATTRIBUTE_FIELDS.has(attr.field),
      )
      .map(attr => ({
        header: attr.title,
        key: attr.field,
        width: 16,
        getValue: row => (row.isTotalRow ? '' : (row[attr.field] ?? row.attributes?.[attr.field] ?? '')),
      }));

    // 4. Daywise Date Headers formatted as 'DD/MM/YYYY' (e.g., '01/09/2026') with adequate column width
    const dayHeaders = dates.map(date => {
      const dateISO = date.format(DATE_FORMAT.ISO);
      return {
        header: date.format('DD/MM/YYYY'),
        key: `date_${dateISO}`,
        width: 14, // Ensures full DD/MM/YYYY date is clearly visible without clipping
        getValue: row => (row.isTotalRow ? dailyTotals[dateISO] : getPlanQuantity(row, dateISO)),
      };
    });

    const allColumns = [
      ...baseHeaders,
      ...mandatoryHeaders,
      ...optionalHeaders,
      ...dayHeaders,
    ];

    const totalRow = {
      isTotalRow: true,
      vc: 'Total',
      description: '',
      total: monthlyTarget,
      balance: monthlyBalance,
      archived: monthlyArchived,
    };

    const exportData = [...dataRows, totalRow];

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Plan');

      worksheet.columns = allColumns.map(col => ({
        header: col.header,
        key: col.key,
        width: col.width,
      }));

      exportData.forEach(row => {
        const rowData = {};
        allColumns.forEach(col => {
          rowData[col.key] = col.getValue(row);
        });
        const addedRow = worksheet.addRow(rowData);
        if (row.isTotalRow) {
          addedRow.font = { bold: true };
        }
      });

      // Style header row: Grey background fill, bold text, centered alignment, borders
      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.eachCell(cell => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' },
        };
        cell.font = {
          bold: true,
          size: 10,
          name: 'Arial',
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFA0A0A0' } },
          left: { style: 'thin', color: { argb: 'FFA0A0A0' } },
          bottom: { style: 'thin', color: { argb: 'FFA0A0A0' } },
          right: { style: 'thin', color: { argb: 'FFA0A0A0' } },
        };
      });

      const fileNamePrefix = isLucknowProlifePlant
        ? `EnginePlan_${plant.label}_${line.label}`
        : `ChassisPlan_${plant.label}_${line.label}`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileNamePrefix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        'Failed to generate or download the Chassis Planning report, error:' +
          buildErrorMessage(error),
      );
    }
  };
  const renderUploadButton = () => {
    // TODO: fix styling to not need div
    if (disableUpload) return <div className={styles.uploadButton} />;
    return (
      <div>
        <AuthEnabledButton
          operation={USER_OPERATIONS.CREATE_CHASSIS_PLAN_QUANTITY}
          isIconButton
          className={styles.uploadButton}
          onClick={showDownloadAndUploadDialog}
          data-testid="upload-btn"
        >
          <SvgIcon className={styles.uploadIcon}>
            <path d={SVG_ICON_PATHS.uploadIcon} />
          </SvgIcon>
          <span className={styles.label}>Upload</span>
        </AuthEnabledButton>
        <AuthEnabledButton
          operation={USER_OPERATIONS.CREATE_CHASSIS_PLAN_QUANTITY}
          isIconButton
          className={styles.downloadButton}
          onClick={() => handleDayWisePlanDownload(planData, selectedOptionalCols)}
          data-testid="download-btn"
        >
          <SvgIcon className={styles.downloadBtnIcon}>
            <path d={SVG_ICON_PATHS.downloadIcon} />
          </SvgIcon>
          <span className={styles.label}>Download</span>
        </AuthEnabledButton>

        <Dialog
          className={styles.uploadPopip}
          open={isDownloadOrUploadDialogOpen}
          onClose={handleDonloadOrUploadDialogueClose}
          aria-labelledby="draggable-dialog-title"
        >
          <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
            <span>
              <AiFillFile className={styles.fileIcon} />
              <span className={styles.title}>Plan </span>
              <MdClose
                className={styles.cancelIcon}
                onClick={handleDonloadOrUploadDialogueClose}
              />
            </span>
          </DialogTitle>
          <DialogContent>
            <div>
              <Button
                className={styles.downloadTemplateButton}
                onClick={handleDownloadTemplateButtonClick}
              >
                <IoMdCloudDownload className={styles.downloadIcon} />

                <span className={styles.label}>
                  <span className={styles.mainText}>
                    Template <sup>**</sup>
                  </span>
                  <span className={styles.subText}>(Excel file)</span>
                </span>
              </Button>

              <Button className={styles.uploadCsvButton}>
                <IoMdCloudUpload className={styles.uploadCsvIcon} />
                <span className={styles.label}>
                  <label className={styles.upTxt} htmlFor="upload-csv">
                    Upload plan
                  </label>
                  <label className={styles.subText}>(CSV file)</label>
                </span>
                <input
                  className={styles.input}
                  type="file"
                  id="upload-csv"
                  accept=".csv"
                  onClick={e => e.stopPropagation()}
                  onChange={e => {
                    const elem = e.target;
                    handleFileUpload(elem.files[0]);
                    elem.value = ''; // reset input element
                    setIsDownloadOrUploadDialogOpen(false);
                  }}
                />
              </Button>
            </div>
            <p className={styles.message}>
              **Please use the Template for uploading new/revised Plans in the
              system.
            </p>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const CallingAverageDisplay = ({ average }) => {
    return (
      <div className={styles.callingCount}>
        <span className={styles.calling}> Calling average</span>
        <span className={styles.avgCount}>
          {average === null ? '- -' : average}
        </span>
      </div>
    );
  };

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  return (
    <>
      <div className={styles.toolBar}>
        {renderUploadButton()}
        {callingAverage !== undefined && (
          <CallingAverageDisplay average={callingAverage} />
        )}
        {!disableMonthPicker && (
          <MonthPicker
            handleSelect={callFunctionConditionallyWithWarningCurry(
              handleMonthSelect,
              hasActiveEdits,
            )}
          />
        )}

        <AttributesSelector
          columns={optionalColumns}
          selectedOptionalCols={selectedOptionalCols}
          applyColumnsSelection={applyColumnsSelection}
          isShiftPresent={false}
        />
        {!disableFilter && (
          <Button
            className={styles.toolButton}
            ref={filterRef}
            onClick={handleFilterOpen}
          >
            <FilterIcon
              className={styles.toolIcon}
              style={{ fontSize: '19px', marginTop: '2px' }}
              fontSize="small"
            />
            Filter
          </Button>
        )}
        <FilterMenu
          open={isFilterOpen}
          handleClose={handleFilterClose}
          anchorEl={filterRef.current}
          handleFilter={handleFilter}
          filterOptions={getFilterOptionsByBU()}
          rows={planData.data}
        />
        <LineSelector hasActiveEdits={hasActiveEdits} />
      </div>
      <span className={styles.captionSmall}>
        {`Last Refreshed: ${timeString} (IST) ${dateString}`}
      </span>
      {restrictNavigationDuringEditing && (
        <Prompt
          when={hasActiveEdits}
          message={() => DISPLAY_MESSAGES.NAVIGATION_WARNING}
        />
      )}
    </>
  );
};

ToolBar.propTypes = {
  optionalColumns: PropTypes.array,
  applyColumnsSelection: PropTypes.func,
  lastUpdatedDate: PropTypes.instanceOf(moment),
  handleFileUpload: PropTypes.func,
  disableUpload: PropTypes.bool,
  selectedOptionalCols: PropTypes.array.isRequired,
  planData: PropTypes.object.isRequired,
  applyFilters: PropTypes.func.isRequired,
  handleMonthSelect: PropTypes.func,
  disableMonthPicker: PropTypes.bool,
  disableFilter: PropTypes.bool,
  restrictNavigationDuringEditing: PropTypes.bool.isRequired,
  hasActiveEdits: PropTypes.bool.isRequired,
  callingAverage: PropTypes.number,
  line: PropTypes.object,
};

export default ToolBar;
